/**
 * Blob interceptor for client-generated CSV data
 *
 * Monkey-patches URL.createObjectURL to intercept CSV blobs
 * created client-side (e.g., for downloads).
 */

import type { InterceptionConfig } from '../types/config';
import type { DataSource } from '../types/api';
import type {
  Interceptor,
  InterceptionHandler,
  InterceptedCSV,
} from './types';
import {
  isCSVMimeType,
  isCSVFilename,
  looksLikeCSV,
  hashCSV,
  suggestTableName,
} from './types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Blob registry to store blob references
 */
interface BlobInfo {
  blob: Blob;
  url: string;
  timestamp: number;
}

/**
 * Blob interceptor class
 */
export class BlobInterceptor implements Interceptor {
  private originalCreateObjectURL: typeof URL.createObjectURL;
  private originalRevokeObjectURL: typeof URL.revokeObjectURL;
  private active: boolean = false;
  private config: Required<InterceptionConfig>;
  private handler: InterceptionHandler;
  private blobRegistry: Map<string, BlobInfo> = new Map();

  constructor(config: Required<InterceptionConfig>, handler: InterceptionHandler) {
    this.originalCreateObjectURL = URL.createObjectURL.bind(URL);
    this.originalRevokeObjectURL = URL.revokeObjectURL.bind(URL);
    this.config = config;
    this.handler = handler;
  }

  /**
   * Start intercepting blob creation
   */
  start(): void {
    if (this.active) {
      console.warn('[BlobInterceptor] Already active');
      return;
    }

    console.log('[BlobInterceptor] Starting interception');

    const self = this;

    // Monkey-patch URL.createObjectURL
    URL.createObjectURL = function patchedCreateObjectURL(
      obj: Blob | MediaSource
    ): string {
      // Call original function
      const url = self.originalCreateObjectURL(obj);

      // Only process Blob objects (not MediaSource)
      if (obj instanceof Blob) {
        // Check if it's a CSV blob
        if (self.isCSVBlob(obj)) {
          // Store blob reference
          self.blobRegistry.set(url, {
            blob: obj,
            url,
            timestamp: Date.now(),
          });

          // Process blob in background
          self.processBlob(obj, url).catch(error => {
            console.error('[BlobInterceptor] Error processing blob:', error);
          });
        }
      }

      return url;
    };

    // Monkey-patch URL.revokeObjectURL to clean up registry
    URL.revokeObjectURL = function patchedRevokeObjectURL(url: string): void {
      // Clean up our registry
      self.blobRegistry.delete(url);

      // Call original function
      self.originalRevokeObjectURL(url);
    };

    this.active = true;
  }

  /**
   * Stop intercepting and restore original functions
   */
  stop(): void {
    if (!this.active) {
      return;
    }

    console.log('[BlobInterceptor] Stopping interception');

    // Restore original functions
    URL.createObjectURL = this.originalCreateObjectURL;
    URL.revokeObjectURL = this.originalRevokeObjectURL;

    // Clear blob registry
    this.blobRegistry.clear();

    this.active = false;
  }

  /**
   * Check if currently intercepting
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Check if blob looks like CSV
   */
  private isCSVBlob(blob: Blob): boolean {
    // Check MIME type
    if (isCSVMimeType(blob.type)) {
      return true;
    }

    // Check if MIME type is in our configured list
    if (blob.type) {
      const normalized = blob.type.toLowerCase().split(';')[0].trim();
      if (this.config.mimeTypes.some(type => normalized === type)) {
        return true;
      }
    }

    // If type is empty or generic, we'll check content later
    // (text/plain, application/octet-stream, or empty)
    if (!blob.type || blob.type === 'text/plain' || blob.type === 'application/octet-stream') {
      return true; // Will verify with content check
    }

    return false;
  }

  /**
   * Process a CSV blob
   */
  private async processBlob(blob: Blob, blobUrl: string): Promise<void> {
    try {
      // Read blob as text
      const data = await blob.text();

      // Skip empty blobs
      if (!data || data.trim().length === 0) {
        return;
      }

      // Heuristic check - does it look like CSV?
      if (!looksLikeCSV(data)) {
        console.log('[BlobInterceptor] Blob does not look like CSV, skipping');
        return;
      }

      // Try to extract filename from stack trace or other sources
      // (This is best-effort - client-generated blobs often don't have filenames)
      const filename = this.extractFilenameFromContext();
      const suggestedName = suggestTableName(filename);

      // Create intercepted CSV object
      const intercepted: InterceptedCSV = {
        id: uuidv4(),
        data,
        size: data.length,
        source: {
          type: 'blob',
          url: blobUrl,
          filename,
          contentType: blob.type || 'text/csv',
        },
        interceptedVia: 'blob',
        timestamp: Date.now(),
        hash: hashCSV(data),
        suggestedName,
      };

      console.log('[BlobInterceptor] CSV blob intercepted:', {
        url: blobUrl,
        size: data.length,
        type: blob.type,
        suggestedName,
      });

      // Call handler
      await this.handler(intercepted);
    } catch (error) {
      console.error('[BlobInterceptor] Error processing CSV blob:', error);
    }
  }

  /**
   * Try to extract filename from current context
   * This is best-effort and may not always work
   */
  private extractFilenameFromContext(): string | undefined {
    try {
      // Check if there's a download attribute in the DOM
      // (This might catch cases where a blob is being prepared for download)
      const downloadLinks = document.querySelectorAll('a[download]');
      if (downloadLinks.length > 0) {
        // Get the most recently modified download link
        const sortedLinks = Array.from(downloadLinks).sort((a, b) => {
          // This is heuristic - we can't know for sure which link is related
          return 0;
        });

        const downloadAttr = sortedLinks[0].getAttribute('download');
        if (downloadAttr && downloadAttr.trim()) {
          return downloadAttr;
        }
      }
    } catch (e) {
      // Ignore errors
    }

    return undefined;
  }

  /**
   * Get blob info by URL
   */
  getBlobInfo(url: string): BlobInfo | undefined {
    return this.blobRegistry.get(url);
  }

  /**
   * Clean up old blob references (garbage collection)
   */
  cleanupOldBlobs(maxAge: number = 60000): void {
    const now = Date.now();
    for (const [url, info] of this.blobRegistry.entries()) {
      if (now - info.timestamp > maxAge) {
        this.blobRegistry.delete(url);
      }
    }
  }
}

/**
 * Create a blob interceptor
 */
export function createBlobInterceptor(
  config: Required<InterceptionConfig>,
  handler: InterceptionHandler
): Interceptor {
  return new BlobInterceptor(config, handler);
}
